import _ from 'lodash';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from 'react';
import {TextLinkButton} from '../components/Button';
import PageSection from '../components/PageSection';
import SectionTitle from '../components/SectionTitle';
import NewsArticle from '../components/NewsArticle';
import Quotes from '../components/Quotes';
import Quote from '../components/Quote';
import NewsBanner from '../components/NewsBanner';
import {NodesMap} from '../components/NodesMap';
import HeroSection from '../components/home/HeroSection';
import HowItWorksSection from '../components/home/HowItWorksSection';
import EnterpriseAIAgentsSection from '../components/home/EnterpriseAIAgentsSection';
import ReadyToBuildSection from '../components/home/ReadyToBuildSection';
import DownloadClientsSection from '../components/home/DownloadClientsSection';
import ValidatorPartnersSection from '../components/home/ValidatorPartnersSection';
import {GhostBlogUrl, GhostContentApiKey} from '../constants';

import quoteLogosCAA from '../assets/logos/quotes/caa@2x.png';
import quoteLogosGoogle from '../assets/logos/quotes/google@2x.png';
import quoteLogosSony from '../assets/logos/quotes/sony@2x.png';

const SOURCE_TAG_PREFIX = /^#?source:\s*/i;

const getPostSource = (post) => {
    const sourceTag = _.find(post.tags, (tag) => SOURCE_TAG_PREFIX.test(tag.name || ''));
    if (!sourceTag) {
        return 'Theta Labs';
    }
    return sourceTag.name.replace(SOURCE_TAG_PREFIX, '').trim() || 'Theta Labs';
};

const mapGhostPostToNewsArticle = (post) => ({
    title: post.title,
    href: post.url,
    date: post.published_at,
    source: getPostSource(post),
    thumbnail: post.feature_image || null,
});

const HomePage = () => {
    const {t} = useTranslation();
    const [showAllNewsArticles, setShowAllNewsArticles] = useState(false);
    const [newsArticles, setNewsArticles] = useState([]);
    const featuresNewsArticle = _.first(newsArticles);
    const [customerLogos, setCustomerLogos] = useState([]);

    const fetchNewsArticles = async () => {
        if (!GhostContentApiKey) {
            console.warn('GhostContentApiKey is missing in src/constants.js');
            return;
        }

        const params = new URLSearchParams({
            key: GhostContentApiKey,
            limit: '100',
            include: 'tags',
            fields: 'title,url,published_at,feature_image',
            order: 'published_at desc',
        });
        const response = await fetch(`${GhostBlogUrl}/ghost/api/content/posts/?${params}`);
        if (!response.ok) {
            console.error('Failed to fetch Ghost blog posts', response.status);
            return;
        }

        const data = await response.json();
        setNewsArticles(_.map(data.posts, mapGhostPostToNewsArticle));
    };

    const fetchCustomerLogos = async () => {
        let url = 'https://theta-mkt-api.thetatoken.org/v1/sheet/edgecloud-customer-logos-carousel';
        const response = await fetch(url);
        setCustomerLogos(await response.json());
    };

    useEffect(() => {
        document.body.style.backgroundColor = '#09090D';

        fetchNewsArticles();
        fetchCustomerLogos();

        return () => {
            document.body.style.backgroundColor = null;
        };
    }, []);

    return (
        <div className={'Page HomePage'}>
            {
                featuresNewsArticle &&
                <NewsBanner {...featuresNewsArticle}/>
            }

            <HeroSection partnerLogos={customerLogos}/>

            <HowItWorksSection/>

            <EnterpriseAIAgentsSection/>

            <ReadyToBuildSection/>

            <div className={'MapAndNewsWrapper'}>
                <PageSection id={'live-nodes'} className={'NodesMapSection'}>
                    <SectionTitle title={t('home.live-nodes')} weight={600} center/>
                    <NodesMap/>
                </PageSection>

                <ValidatorPartnersSection/>

                {
                    !_.isEmpty(newsArticles) &&
                    <PageSection className={'NewsSection'}>
                        <SectionTitle title={t('home.recent-news')} weight={600} center/>
                        {/* <TextLinkButton className={'NewsSection__view-all-news'}
                                        onClick={() => {
                                            setShowAllNewsArticles(!showAllNewsArticles);
                                        }}
                        >
                            {!showAllNewsArticles && t('home.view-more-news')}
                            {showAllNewsArticles && t('home.view-less-news')}
                        </TextLinkButton> */}

                        <div className={'NewsArticles'}>
                            {
                                _.map(_.take(newsArticles, (showAllNewsArticles ? 36 : 9)), (newsArticle) => {
                                    return <NewsArticle {...newsArticle}/>;
                                })
                            }
                        </div>
                        <TextLinkButton className={'NewsSection__view-all-news'}
                                        onClick={() => {
                                            setShowAllNewsArticles(!showAllNewsArticles);
                                        }}
                        >
                            {!showAllNewsArticles && t('home.view-more-news')}
                            {showAllNewsArticles && t('home.view-less-news')}
                        </TextLinkButton>
                    </PageSection>
                }
            </div>

            <DownloadClientsSection/>

            <PageSection className={'QuotesSection'}>
                <Quotes center>
                    <Quote body={t('quotes.caa.body')}
                           name={t('quotes.caa.name')}
                           logo={quoteLogosCAA}
                           center
                    />
                    <Quote body={t('quotes.google.body')}
                           logo={quoteLogosGoogle}
                           center
                    />
                    <Quote body={t('quotes.sony.body')}
                           logo={quoteLogosSony}
                           center
                    />
                </Quotes>
            </PageSection>
        </div>
    );
};

export default HomePage;
